import { useState, type FC, useEffect } from 'react';
import { datadogRum } from '@datadog/browser-rum';
import { useNavigate } from 'react-router-dom';
import { observer } from 'mobx-react-lite';
import { isEmpty, isString } from 'lodash';
import { useMutation } from '@tanstack/react-query';
import { Space } from '@g2u/ui-kit';
import { Alert, Form } from 'antd';

import { Actions, ServiceSelection, ServiceLocation, JobDetails } from './components';
import { useExtraQuestionsData, useServiceSelection, useProductList } from './hooks';
import { AccountType, PricingCategory, type JsonDataItem } from 'types';
import { postServiceSelection, putServiceSelection } from 'queries';
import { parseErrors, Toast, validateFormSilently } from 'helpers';
import { AccountTypeFields, Content, Intro } from 'components';
import { BookingStore } from 'store';
import styles from './location.module.scss';

export const BookingLocationPage: FC = observer(() => {
  const {
    customer,
    setServiceSelection,
    productSelection,
    setProductSelection,
    setStep,
    serviceSelection,
    serviceList,
    isNewCustomer,
    callback,
  } = BookingStore;
  const navigate = useNavigate();
  const [form] = Form.useForm();
  const productFieldValue = Form.useWatch('products', { form });

  const [isFormValid, setIsFormValid] = useState<boolean>(false);
  const { getExtraQuestionsData } = useExtraQuestionsData();
  const { isServiceListLoading, serviceListError, refetchServiceList } = useServiceSelection(callback);
  const {
    isProductListLoading,
    productListError,
    getProductList,
    prepaidProductsIds,
    prepaidProducts,
    hasMultiplePrepaidSkills,
  } = useProductList(customer?.ID, callback?.record.product_ID);

  const { mutate: saveServiceSelection, isLoading: savingServiceSelection } = useMutation(
    serviceSelection ? putServiceSelection : postServiceSelection,
    {
      onSuccess: (data) => {
        setServiceSelection(data.service_selection);
        data.extra_job_description_added &&
          Toast.show({
            description: 'The scope of the job has been automatically added to the job description',
            type: Toast.Type.Success,
          });
        navigate('/booking/technician');
      },
      onError: (data: JsonDataItem) => {
        Toast.show({ description: parseErrors(data).join(', '), type: Toast.Type.Error });
      },
    },
  );

  const handleFormChange = (field: JsonDataItem, fields: JsonDataItem) => {
    // field.products doesn't reflected here (only search value)
    !isString(field.products) && setProductSelection(fields);

    // update pricing category and refetch product list if account type changed
    if (field.accountType) {
      const newPricingCategory =
        field.accountType === AccountType.Company ? PricingCategory.Business : PricingCategory.Home;
      form.setFieldValue('pricingCategory', newPricingCategory);
      getProductList({ ...fields, pricingCategory: newPricingCategory });
    }
    // refetch product list if pricing category changed
    if (field.pricingCategory || field.serviceType) {
      getProductList(fields);
    }
    void (!isEmpty(form.getFieldsValue()) && validateFormSilently(form, setIsFormValid));
  };

  useEffect(() => {
    productFieldValue && !isString(productFieldValue) && setProductSelection(form.getFieldsValue());
    void (!isEmpty(form.getFieldsValue()) && validateFormSilently(form, setIsFormValid));
  }, [productFieldValue]);

  useEffect(() => {
    form.getFieldValue('products') &&
      !isString(form.getFieldValue('products')) &&
      setProductSelection(form.getFieldsValue());
  }, [isServiceListLoading]);

  const handleFormFinish = (data: JsonDataItem) => {
    setProductSelection(data);
    saveServiceSelection({
      products: data.products,
      selectionId: serviceSelection?.ID,
      customerId: customer?.ID,
      devices: data.device.map((name: string) => {
        return { id: serviceList?.parent.options.find((device: JsonDataItem) => device.name === name)?.id, qty: 1 };
      }),
      os: data.os?.map((os: string) => {
        return { [os]: 1 };
      }),
      description: data.description,
      additionalProblem: data.additional_problem,
      serviceType: data.serviceType,
      previousJob: data.jobId,
      warrantyReason: data.warrantyReason,
      productAdditionalSelection: getExtraQuestionsData(data),
    });
  };

  useEffect(() => {
    customer && datadogRum.setGlobalContextProperty('custid', String(customer.ID));
    setStep(1);
    void validateFormSilently(form, setIsFormValid);
    getProductList(form.getFieldsValue());
    return () => {
      setStep(0);
    };
  }, []);

  return (
    <Content className={styles.location} dataTestId="location-page">
      <title>Confirm location & job details</title>
      <Form
        form={form}
        colon={false}
        layout="vertical"
        onValuesChange={handleFormChange}
        onFinish={handleFormFinish}
        style={{ width: '100%' }}
      >
        <Space.$ size={Space.Size.Large} direction={Space.Direction.Vertical} fullWidth>
          <Intro />
          <ServiceLocation form={form} />
          {hasMultiplePrepaidSkills && prepaidProducts && (
            <Alert
              message={`The customer has purchased ${prepaidProducts
                .map((product: JsonDataItem) => `‘${product.product_name.trim()}’`)
                .join(
                  ' and ',
                )} through Officeworks. You need to make separate bookings since they require different technicians.`}
              type="warning"
              style={{ borderRadius: 8 }}
              showIcon
            />
          )}
          <JobDetails form={form} />
          {isNewCustomer && (
            <AccountTypeFields
              hasScript
              customer={customer}
              title={'Customer Account Type'}
              initialValues={{
                accountType: productSelection?.accountType,
                pricingCategory: productSelection?.pricingCategory || PricingCategory.Home,
              }}
            />
          )}
          <ServiceSelection
            form={form}
            isLoading={isProductListLoading || isServiceListLoading}
            productListError={productListError}
            refetchProductList={() => getProductList(form.getFieldsValue())}
            serviceSelectionError={serviceListError}
            refetchServiceSelection={refetchServiceList}
            prepaidProductsIds={prepaidProductsIds}
          />

          <Actions
            active={isFormValid}
            isLoading={savingServiceSelection}
            onNext={() => {
              form.submit();
            }}
          />
        </Space.$>
      </Form>
    </Content>
  );
});
